import React, { useState } from 'react'
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'
import { getInitials } from '@ppwr/poker'
import { hasAdminRights, hasOwnerRights, type LoungeRole } from '@ppwr/convex/lounges/shared'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { translate } from '../../../i18n'
import { LoungeCodeCard } from './LoungeCodeCard'
import { MemberSettingsSheet } from './MemberSettingsSheet'
import { memberAvatarColor } from './loungeChatUtils'
import type { LoungeMember as Member } from '../types'

export type LoungeMembersTabProps = {
  loungeId: Id<'lounges'>
  members: Member[]
  currentUserId: string
  currentUserName?: string
  callerRole?: LoungeRole | null
  joinCode?: string | null
  onRotateCode?: () => void
  /** Lets owners edit the join code inline (see `LoungeCodeCard`). */
  onSaveCode?: (code: string) => Promise<void>
  rotating?: boolean
}

// ─── Status dot ──────────────────────────────────────────────────────────────

type DotStatus = 'online' | 'inGame' | 'offline'

function dotStatus(m: Member): DotStatus {
  if (m.isInGame) return 'inGame'
  if (m.isOnline) return 'online'
  return 'offline'
}

function StatusDot({ status }: { status: DotStatus }) {
  const color =
    status === 'online'
      ? DS.palette['Utility/Success']
      : status === 'inGame'
        ? DS.palette['Brand/Blue']
        : DS.palette['Brand/Light Grey']
  return <View style={[styles.dot, { backgroundColor: color }]} />
}

// ─── Role badge ───────────────────────────────────────────────────────────────

function roleBadgeLabel(role: LoungeRole): string | null {
  if (role === 'owner') return 'Owner'
  if (role === 'coowner') return 'Co-Owner'
  return null
}

function RoleBadge({ role }: { role: LoungeRole }) {
  const label = roleBadgeLabel(role)
  if (!label) return null
  return (
    <DS.Text variant="Caption/Regular" style={styles.roleBadge}>
      {label}
    </DS.Text>
  )
}

// ─── Status legend ────────────────────────────────────────────────────────────

function StatusLegend() {
  return (
    <View style={styles.legend}>
      <DS.Text variant="Mini/Bold" style={styles.legendLabel} uppercase>
        {translate('loungeLobbyScreen.membersStatusLabel')}
      </DS.Text>
      <View style={styles.legendItems}>
        <View style={styles.legendItem}>
          <StatusDot status="online" />
          <DS.Text variant="Caption/Regular" style={styles.legendText}>
            {translate('loungeLobbyScreen.membersStatusOnline')}
          </DS.Text>
        </View>
        <View style={styles.legendItem}>
          <StatusDot status="inGame" />
          <DS.Text variant="Caption/Regular" style={styles.legendText}>
            {translate('loungeLobbyScreen.membersStatusInGame')}
          </DS.Text>
        </View>
        <View style={styles.legendItem}>
          <StatusDot status="offline" />
          <DS.Text variant="Caption/Regular" style={styles.legendText}>
            {translate('loungeLobbyScreen.membersStatusOffline')}
          </DS.Text>
        </View>
      </View>
    </View>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LoungeMembersTab({
  loungeId,
  members,
  currentUserId,
  currentUserName,
  callerRole,
  joinCode,
  onRotateCode,
  rotating,
  onSaveCode
}: LoungeMembersTabProps) {
  const isAdmin = callerRole ? hasAdminRights(callerRole) : false
  const canRotate = callerRole ? hasOwnerRights(callerRole) : false

  const [selectedMember, setSelectedMember] = useState<Member | null>(null)

  return (
    <View style={styles.flex}>
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {/* Members card */}
        <View style={styles.card}>
          <View style={styles.memberList}>
            {members.map((member) => {
              const isMe = member.userId === currentUserId
              const displayName = isMe ? (currentUserName ?? 'You') : (member.userName ?? 'Member')
              const status = dotStatus(member)
              const showOverflow = isAdmin && member.role !== 'owner'

              return (
                <View key={member.userId} style={styles.row}>
                  {/* Avatar */}
                  <View style={[styles.avatar, { backgroundColor: memberAvatarColor(member.userId) }]}>
                    <DS.Text variant="Mini/Bold" style={styles.avatarText}>
                      {getInitials(displayName)}
                    </DS.Text>
                  </View>

                  {/* Name */}
                  <DS.Text variant="Body Large/Medium" numberOfLines={1} style={styles.name}>
                    {displayName}
                  </DS.Text>

                  {/* Role badge — fixed-width slot keeps the status dot column aligned */}
                  <View style={styles.badgeSlot}>
                    <RoleBadge role={member.role} />
                  </View>

                  {/* Status dot */}
                  <StatusDot status={status} />

                  {/* Overflow menu — owner row is always excluded */}
                  {showOverflow ? (
                    <Pressable onPress={() => setSelectedMember(member)} hitSlop={8} style={styles.overflowButton}>
                      <Text style={styles.overflowDots}>•••</Text>
                    </Pressable>
                  ) : (
                    <View style={styles.overflowPlaceholder} />
                  )}
                </View>
              )
            })}
          </View>

          {/* Legend */}
          <StatusLegend />
        </View>

        {/* Share the Lounge card — admins only */}
        {isAdmin && (
          <LoungeCodeCard
            mode="share"
            joinCode={joinCode ?? null}
            onRotateCode={onRotateCode}
            canRotate={canRotate}
            rotating={rotating}
            loungeId={loungeId}
            onSaveCode={canRotate ? onSaveCode : undefined}
          />
        )}
      </ScrollView>

      {isAdmin && callerRole && (
        <MemberSettingsSheet
          visible={selectedMember !== null}
          member={selectedMember}
          loungeId={loungeId}
          callerUserId={currentUserId}
          callerRole={callerRole}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  flex: {
    flex: 1
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 32,
    gap: 24
  },
  card: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 20,
    gap: 20
  },
  memberList: {
    gap: 16
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center'
  },
  avatarText: {
    color: DS.palette['Global/White'],
    fontSize: 14
  },
  name: {
    flex: 1
  },
  badgeSlot: {
    width: 80,
    alignItems: 'flex-end'
  },
  roleBadge: {
    color: DS.palette['Brand/Orange'],
    fontWeight: '500'
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingTop: 4
  },
  legendLabel: {
    color: DS.palette['Brand/Dark Grey'],
    letterSpacing: 0.5
  },
  legendItems: {
    flexDirection: 'row',
    gap: 16
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6
  },
  legendText: {
    color: DS.palette['Global/Black']
  },
  overflowButton: {
    padding: 4
  },
  overflowDots: {
    fontSize: 16,
    color: DS.palette['Brand/Dark Grey'],
    letterSpacing: 1
  },
  overflowPlaceholder: {
    width: 32
  }
})
